import type { ComponentAttributesStoreState } from '../stores/componentAttributes.svelte'
import { parseBoolean, parseNumber, parseString } from '../utils/Parsers'

export enum AttributeType {
  STRING,
  COLOR,
  BOOLEAN,
  NUMBER,
}
export interface AttributeStoreMappingEntry<T> {
  parse: (value: string | null) => T
  property: string
  type: AttributeType
}

export type AttributeName =
  | 'id'
  | 'domain'
  | 'autoplay'
  | 'playback-rate'
  | 'width'
  | 'height'
  | 'theme-primary-color'
  | 'theme-buttons-color' // = "Link color" in legacy
  | 'theme-buttons-hover-color' // = "Link hover color" in legacy
  | 'theme-play-button-color' // = "Link active color" in legacy
  | 'theme-progress-bar-color' // = "Track played color" in legacy
  | 'theme-title-color' // = "Background text color" in legacy
  | 'theme-overlay-text-color' // = "Background text color" in legacy
  | 'theme-overlay-background-top-color' // = "Background top color" in legacy
  | 'theme-overlay-background-bottom-color' // = "Background bottom color" in legacy
  | 'muted'
  | 'volume'
  | 'hide-poster'
  | 'hide-controls'
  | 'hide-title'
  | 'show-captions'
  | 'loop'
  | 'force-mobile-layout'
  | 'media-title'
  | 'disable-hotkeys'
  | 'disable-download'
  | 'sequence-start'
  | 'sequence-end'
  | 'disable-quality-picker'
  | 'max-quality'
  | 'ui-language'
  | 'collector-domain'
  | 'abuse-domain'

export const parsers: Record<AttributeType, (value: string | null) => any> = {
  [AttributeType.STRING]: parseString,
  [AttributeType.COLOR]: parseString,
  [AttributeType.BOOLEAN]: parseBoolean,
  [AttributeType.NUMBER]: parseNumber,
}

export const attrToStorePropertyMap: {
  [K in AttributeName]: {
    storeProperty: keyof ComponentAttributesStoreState
    type: AttributeType
  }
} = {
  id: {
    storeProperty: 'id',
    type: AttributeType.STRING,
  },
  domain: {
    storeProperty: 'domain',
    type: AttributeType.STRING,
  },
  width: {
    storeProperty: 'width',
    type: AttributeType.STRING,
  },
  height: {
    storeProperty: 'height',
    type: AttributeType.STRING,
  },
  'playback-rate': {
    storeProperty: 'playbackRate',
    type: AttributeType.NUMBER,
  },
  volume: {
    storeProperty: 'volume',
    type: AttributeType.NUMBER,
  },
  'ui-language': {
    storeProperty: 'uiLanguage',
    type: AttributeType.STRING,
  },
  'theme-primary-color': {
    storeProperty: 'primaryColor',
    type: AttributeType.COLOR,
  },
  'theme-buttons-color': {
    storeProperty: 'buttonsColor',
    type: AttributeType.COLOR,
  },
  'theme-buttons-hover-color': {
    storeProperty: 'buttonsHoverColor',
    type: AttributeType.COLOR,
  },
  'theme-play-button-color': {
    storeProperty: 'playButtonColor',
    type: AttributeType.COLOR,
  },
  'theme-progress-bar-color': {
    storeProperty: 'progressBarColor',
    type: AttributeType.COLOR,
  },
  'theme-title-color': {
    storeProperty: 'titleColor',
    type: AttributeType.COLOR,
  },
  'theme-overlay-text-color': {
    storeProperty: 'overlayTextColor',
    type: AttributeType.COLOR,
  },
  'theme-overlay-background-top-color': {
    storeProperty: 'overlayBackgroundTopColor',
    type: AttributeType.COLOR,
  },
  'theme-overlay-background-bottom-color': {
    storeProperty: 'overlayBackgroundBottomColor',
    type: AttributeType.COLOR,
  },
  'media-title': {
    storeProperty: 'mediaTitle',
    type: AttributeType.STRING,
  },
  muted: {
    storeProperty: 'muted',
    type: AttributeType.BOOLEAN,
  },
  autoplay: {
    storeProperty: 'autoplay',
    type: AttributeType.BOOLEAN,
  },
  'hide-controls': {
    storeProperty: 'hideControls',
    type: AttributeType.BOOLEAN,
  },
  'hide-title': {
    storeProperty: 'hideTitle',
    type: AttributeType.BOOLEAN,
  },
  'hide-poster': {
    storeProperty: 'hidePoster',
    type: AttributeType.BOOLEAN,
  },
  'show-captions': {
    storeProperty: 'showCaptions',
    type: AttributeType.BOOLEAN,
  },
  loop: {
    storeProperty: 'loop',
    type: AttributeType.BOOLEAN,
  },
  'force-mobile-layout': {
    storeProperty: 'forceMobileLayout',
    type: AttributeType.BOOLEAN,
  },
  'disable-hotkeys': {
    storeProperty: 'disableHotkeys',
    type: AttributeType.BOOLEAN,
  },
  'disable-download': {
    storeProperty: 'disableDownload',
    type: AttributeType.BOOLEAN,
  },
  'sequence-start': {
    storeProperty: 'sequenceStart',
    type: AttributeType.NUMBER,
  },
  'sequence-end': {
    storeProperty: 'sequenceEnd',
    type: AttributeType.NUMBER,
  },
  'disable-quality-picker': {
    storeProperty: 'disableQualityPicker',
    type: AttributeType.BOOLEAN,
  },
  'max-quality': {
    storeProperty: 'maxQuality',
    type: AttributeType.NUMBER,
  },
  'collector-domain': {
    storeProperty: 'collectorDomain',
    type: AttributeType.STRING,
  },
  'abuse-domain': {
    storeProperty: 'abuseDomain',
    type: AttributeType.STRING,
  },
}

export const attributeStoreMapping: {
  [K in AttributeName]: AttributeStoreMappingEntry<any>
} = {} as any

for (const attrName of Object.keys(attrToStorePropertyMap) as AttributeName[]) {
  const storeProperty = attrToStorePropertyMap[attrName]

  attributeStoreMapping[attrName] = {
    parse: parsers[storeProperty.type],
    property: storeProperty.storeProperty,
    type: storeProperty.type,
  }
}
